import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpResponseHeaders,
  NamingStrategy
} from '@/data/protocols/http'
import { getSessionId } from '@/data/services/login/session-id-store'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'
import { NetworkError } from '@/infra/http/network-error'
import { i18nTranslationHelper } from '@/i18n'
import { settings } from '@/main/config/settings'
import {
  getRecaptchaSiteKey,
  getRecaptchaToken,
  setRecaptchaSiteKey,
  setRecaptchaToken
} from '@/presentation/contexts/recaptcha/recaptcha-token-store'
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'

const isInternalRequest = (url?: string): boolean =>
  !!url && !!settings.BASE_URL && url.startsWith(settings.BASE_URL)

const camelizeResponse = (response: AxiosResponse): AxiosResponse => {
  if (response.data && response.headers['content-type'] === 'application/json') {
    response.data = camelizeKeys(response.data)
  }
  return response
}

axios.interceptors.response.use(camelizeResponse, (error: AxiosError) => {
  if (error.response) {
    camelizeResponse(error.response)
  }
  return Promise.reject(error)
})

axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config }

  if (isInternalRequest(newConfig.url)) {
    const language = i18nTranslationHelper.getCurrentLanguage()
    if (language) {
      newConfig.headers['Accept-Language'] = language
    }

    const recaptchaToken = getRecaptchaToken()
    if (recaptchaToken) {
      newConfig.headers['X-Recaptcha-Token'] = recaptchaToken
      setRecaptchaToken(null)
    }

    const recaptchaSiteKey = getRecaptchaSiteKey()
    if (recaptchaSiteKey) {
      newConfig.headers['X-Recaptcha-Site-Key'] = recaptchaSiteKey
      setRecaptchaSiteKey(null)
    }

    const sessionId = getSessionId()
    if (sessionId) {
      newConfig.headers['X-Session-Id'] = sessionId
      newConfig.headers['X-Correlation-Id'] = sessionId + '_' + crypto.randomUUID()
    }
  }

  if (!config.headers['Content-Type'] || config.headers['Content-Type'] === 'application/json') {
    if (config.params) {
      newConfig.params =
        newConfig.paramsNamingStrategy === NamingStrategy.snakeCase
          ? decamelizeKeys(newConfig.params)
          : camelizeKeys(newConfig.params)
    }
    if (config.data && !(config.data instanceof FormData)) {
      newConfig.data = decamelizeKeys(config.data)
    }
  }

  return newConfig
})

export class AxiosHttpClient implements HttpClient {
  async request(data: HttpRequest): Promise<HttpResponse> {
    let axiosResponse: AxiosResponse
    try {
      axiosResponse = await axios.request({
        url: data.url,
        method: data.method,
        data: data.body,
        headers: data.headers,
        paramsNamingStrategy: data.paramsNamingStrategy,
        params: data.params,
        withCredentials: data.withCredentials,
        onUploadProgress:
          data.onUploadProgress &&
          (progressEvent => data.onUploadProgress!(((progressEvent.progress || 0) * 100) | 0)),
        signal: data.signal ? data.signal : undefined,
        responseType: data.responseType
      })
    } catch (error) {
      // Cancelamento (ex.: navegação/AbortSignal) não é erro de rede: propaga.
      if (axios.isCancel(error)) {
        throw error
      }

      // Erro local (ex.: TypeError em interceptor/callback) não é falha de rede:
      // propaga para não mascarar um bug como "sem conexão" (warning no Sentry).
      if (!axios.isAxiosError(error)) {
        throw error
      }

      if (!error.response) {
        // Sem resposta HTTP: rede/CORS/timeout ou um intermediário (proxy
        // corporativo / WAF) bloqueou a request antes de chegar à nossa API.
        // Kill-switch: ligado → lança NetworkError (observável + mensagem
        // amigável, válido para TODOS os clientes, inclusive httpServiceClient);
        // desligado → comportamento legado. O correlationId e a URL vivem em
        // config mesmo sem resposta.
        if (checkFlagEnabled(FeatureFlagName.ENABLE_NETWORK_ERROR_HANDLING)) {
          // Headers podem ser normalizados para minúsculas pelo Axios — checa
          // ambas as caixas para não perder o correlationId.
          throw new NetworkError(
            undefined,
            (error.config?.headers?.['X-Correlation-Id'] ??
              error.config?.headers?.['x-correlation-id']) as string | undefined,
            error.config?.url ?? data.url
          )
        }
      }

      axiosResponse = error.response!
    }

    return {
      statusCode: axiosResponse?.status,
      body: axiosResponse?.data,
      headers: axiosResponse?.headers as HttpResponseHeaders,
      correlationId: axiosResponse?.config?.headers?.['X-Correlation-Id']
    }
  }
}
